import type { components, paths } from '../types/api/generated';
import { ffhClient } from '@lib/api/ffh-client';
import { authHeader } from '@lib/api/auth-header';

export type PlayerStatsResponse = components['schemas']['PlayerStatsResponse'];
export type PlayerStatsRow = PlayerStatsResponse['data'][number];
export type PlayerStatsQuery = NonNullable<
  paths['/league/players/stats']['get']['parameters']['query']
>;
export type PlayerStatsAggregation = NonNullable<
  PlayerStatsQuery['aggregation']
>;
export type PlayerStatsVenue = NonNullable<PlayerStatsQuery['venue']>;
export type PlayerStatsPosition = NonNullable<PlayerStatsQuery['position']>;
export type PlayerStatsSortDirection = NonNullable<
  PlayerStatsQuery['sortDirection']
>;

export type GetPlayerStatsOptions = {
  accessToken?: string;
  fields: string;
  cursor?: string;
  limit?: number;
  ids?: string;
  position?: PlayerStatsPosition;
  teamId?: string;
  aggregation?: PlayerStatsAggregation;
  venue?: PlayerStatsVenue;
  season?: number | null;
  minGameweek?: number;
  maxGameweek?: number;
  minPrice?: number | null;
  maxPrice?: number | null;
  minDifficulty?: number;
  maxDifficulty?: number;
  minMinutes?: number | null;
  hideMinutes?: boolean;
  perMillion?: boolean;
  search?: string;
  sortBy?: string;
  sortDirection?: PlayerStatsSortDirection;
};

const boolToFlag = (value: boolean | undefined): 'true' | 'false' | undefined =>
  value === undefined ? undefined : value ? 'true' : 'false';

const buildQuery = ({
  fields,
  cursor,
  limit,
  ids,
  position,
  teamId,
  aggregation,
  venue,
  season,
  minGameweek,
  maxGameweek,
  minPrice,
  maxPrice,
  minDifficulty,
  maxDifficulty,
  minMinutes,
  hideMinutes,
  perMillion,
  search,
  sortBy,
  sortDirection
}: GetPlayerStatsOptions): PlayerStatsQuery => {
  const hideMinutesFlag = boolToFlag(hideMinutes);
  const perMillionFlag = boolToFlag(perMillion);

  return {
    fields,
    ...(cursor !== undefined && { after: cursor }),
    ...(limit !== undefined && { limit }),
    ...(ids !== undefined && { ids }),
    ...(position !== undefined && { position }),
    ...(teamId !== undefined && { teamId }),
    ...(aggregation !== undefined && { aggregation }),
    ...(venue !== undefined && { venue }),
    ...(season !== undefined && season !== null && { season }),
    ...(minGameweek !== undefined && { minGameweek }),
    ...(maxGameweek !== undefined && { maxGameweek }),
    ...(minPrice !== undefined && { minPrice }),
    ...(maxPrice !== undefined && { maxPrice }),
    ...(minDifficulty !== undefined && { minDifficulty }),
    ...(maxDifficulty !== undefined && { maxDifficulty }),
    ...(minMinutes !== undefined && minMinutes !== null && { minMinutes }),
    ...(hideMinutesFlag !== undefined && { hideMinutes: hideMinutesFlag }),
    ...(perMillionFlag !== undefined && { perMillion: perMillionFlag }),
    ...(search !== undefined && search.length > 0 && { search }),
    ...(sortBy !== undefined && { sortBy }),
    ...(sortDirection !== undefined && { sortDirection })
  };
};

export const fetchPlayerStats = async (
  options: GetPlayerStatsOptions
): Promise<PlayerStatsResponse> => {
  const { accessToken } = options;
  const { data, error } = await ffhClient().GET('/league/players/stats', {
    params: { query: buildQuery(options) },
    ...(accessToken ? { headers: authHeader(accessToken) } : {})
  });

  if (error) throw new Error('Failed to load player stats');
  return data;
};
